// src/components/Footer.tsx
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Heart } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const Footer = () => {
  return (
    <footer className="bg-stone-900 text-white pt-16 pb-8">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Contact Info */}
          <Card className="bg-stone-800 border-stone-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(240,90,40,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-secondary">Kamen d.o.o.</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-secondary mt-1" />
                  <div>
                    <p className="font-medium text-secondary">Salon keramike in kamna</p>
                    <p className="text-stone-300">Ptujska cesta 110</p>
                    <p className="text-stone-300">2000 Maribor</p>
                    <p className="text-stone-300">Slovenija</p>
                    <a 
                      href="https://maps.google.com/?q=Ptujska+cesta+110,+2000+Maribor,+Slovenia" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs text-secondary hover:text-white transition-colors mt-1 inline-block"
                    >
                      Odpri v Google Maps
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-secondary mt-1" />
                  <div>
                    <p className="font-medium text-secondary">Proizvodnja</p>
                    <p className="text-stone-300">Levarska ulica 3</p>
                    <p className="text-stone-300">2314 Zgornja Polskava</p>
                    <p className="text-stone-300">Slovenija</p>
                    <a 
                      href="https://maps.google.com/?q=Levarska+ulica+3,+2314+Zgornja+Polskava,+Slovenia" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs text-secondary hover:text-white transition-colors mt-1 inline-block"
                    >
                      Odpri v Google Maps
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Phone size={20} className="text-secondary" />
                  <a href="tel:031467272" className="footer-link">031 467 272</a>
                </div>
                
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-secondary" />
                  <a href="mailto:info@kamen.si" className="footer-link">info@kamen.si</a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card className="bg-stone-800 border-stone-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(240,90,40,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-secondary">Raziskujte</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="footer-link">Domov</Link></li>
                <li><Link to="/keramika" className="footer-link">Keramika</Link></li>
                <li><Link to="/kamen" className="footer-link">Kamen</Link></li>
                <li><Link to="/pulti" className="footer-link">Pulti</Link></li>
                <li><Link to="/storitve" className="footer-link">Storitve</Link></li>
                <li><Link to="/nasveti-blog" className="footer-link">Nasveti/Blog</Link></li>
                <li><Link to="/kontakt" className="footer-link">Kontakt</Link></li>
                <li><Link to="/cookies" className="footer-link">Piškotki</Link></li>
                <li><Link to="/gdpr" className="footer-link">GDPR</Link></li>
              </ul>
            </CardContent>
          </Card>

          {/* Storitve */}
          <Card className="bg-stone-800 border-stone-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(240,90,40,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-secondary">Storitve</h3>
              <ul className="space-y-2">
                <li><Link to="/storitve" className="footer-link">Gradbeni stroji</Link></li>
                <li><Link to="/storitve" className="footer-link">Prevozi</Link></li>
                <li><Link to="/storitve" className="footer-link">Montaža</Link></li>
                <li><Link to="/kontakt" className="footer-link">Zahtevaj ponudbo</Link></li>
              </ul>
            </CardContent>
          </Card>

          {/* Social Media */}
          <Card className="bg-stone-800 border-stone-700 rounded-3xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(240,90,40,0.5)] hover:border-secondary">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4 text-secondary">Sledite nam</h3>
              <div className="flex space-x-4 mb-6">
                <a 
                  href="#" 
                  className="bg-stone-700 hover:bg-secondary rounded-full transition-colors"
                  aria-label="Facebook"
                >
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-secondary hover:text-white h-10 w-10 rounded-full"
                  >
                    <Facebook size={20} />
                  </Button>
                </a>
                <a 
                  href="#" 
                  className="bg-stone-700 hover:bg-secondary rounded-full transition-colors"
                  aria-label="Instagram"
                >
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-secondary hover:text-white h-10 w-10 rounded-full"
                  >
                    <Instagram size={20} />
                  </Button>
                </a>
              </div>
              <p className="text-stone-300 text-sm">
                Spremljajte nas na družbenih omrežjih za najnovejše novice, posebne ponudbe in inspiracijo.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="border-t border-stone-700 mt-10 pt-6 text-center text-stone-400 text-sm">
          <p>© 2025 Kamen d.o.o. Vse pravice pridržane.</p>
          <p className="mt-2 flex items-center justify-center gap-1">
            Made with <Heart size={16} className="text-red-500 animate-pulse" /> by <a href="https://digitalpro.dev/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-white transition-colors">DigitalPro</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;